// Endpoints, tokens e preferências locais.
// Cada endpoint é um AgentOS instance (Salvador, Socorro, Juarez, etc).

import type { TeamSummary, AgentSummary } from '../types/agno-api';

export interface Endpoint {
  url: string;
  label?: string;
  token?: string;
  catalog?: {
    teams: TeamSummary[];
    agents: AgentSummary[];
    fetchedAt: number;
  };
}

export interface ActiveRef {
  endpointUrl: string;
  targetId: string;
  isTeam: boolean;
}

const KEYS = {
  endpoints: 'agents-chat.endpoints',
  active: 'agents-chat.active',
  userId: 'agents-chat.userId',
  theme: 'agents-chat.theme',
  showSteps: 'agents-chat.showSteps',
  showReasoning: 'agents-chat.showReasoning',
  showSupervisor: 'agents-chat.showSupervisor',
  chatSessions: 'agents-chat.chatSessions',
} as const;

// Seed inicial. Token vazio — usuário cola na primeira seleção.
const DEFAULT_ENDPOINTS: Endpoint[] = [
  { url: 'https://salvador.blips-labs.com', label: 'Salvador' },
];

// URLs descontinuadas que foram defaults em versões anteriores. Se aparecem no
// localStorage de um browser antigo, removemos no boot — DNS não existe mais.
// Bump esta lista quando aposentar um endpoint default.
const DEPRECATED_URLS: ReadonlySet<string> = new Set([
  'https://socorro.blips-labs.com',
  'https://juarez.blips-labs.com',
]);

export function getEndpoints(): Endpoint[] {
  const raw = localStorage.getItem(KEYS.endpoints);
  if (!raw) {
    saveEndpoints(DEFAULT_ENDPOINTS);
    return DEFAULT_ENDPOINTS;
  }
  try {
    const stored = JSON.parse(raw) as Endpoint[];
    return migrateEndpoints(stored);
  } catch {
    return DEFAULT_ENDPOINTS;
  }
}

/** Remove endpoints descontinuados e dedupe por URL. Se sobrar nada, restaura defaults. */
function migrateEndpoints(stored: Endpoint[]): Endpoint[] {
  const seen = new Set<string>();
  const out: Endpoint[] = [];
  let changed = false;
  for (const ep of stored) {
    if (!ep?.url) {
      changed = true;
      continue;
    }
    if (DEPRECATED_URLS.has(ep.url)) {
      changed = true;
      continue;
    }
    if (seen.has(ep.url)) {
      changed = true;
      continue;
    }
    seen.add(ep.url);
    out.push(ep);
  }
  // Garante que defaults atuais estão presentes (mas só os que ainda existem).
  for (const def of DEFAULT_ENDPOINTS) {
    if (!seen.has(def.url)) {
      out.unshift(def);
      changed = true;
    }
  }
  const final = out.length > 0 ? out : DEFAULT_ENDPOINTS;
  if (changed) saveEndpoints(final);
  return final;
}

export function saveEndpoints(eps: Endpoint[]): void {
  localStorage.setItem(KEYS.endpoints, JSON.stringify(eps));
}

export function upsertEndpoint(ep: Endpoint): Endpoint[] {
  const cleaned = { ...ep, url: ep.url.replace(/\/$/, '') };
  const list = getEndpoints();
  const idx = list.findIndex((e) => e.url === cleaned.url);
  if (idx >= 0) list[idx] = { ...list[idx], ...cleaned };
  else list.push(cleaned);
  saveEndpoints(list);
  return list;
}

export function removeEndpoint(url: string): Endpoint[] {
  const list = getEndpoints().filter((e) => e.url !== url);
  saveEndpoints(list);
  return list;
}

export function getActive(): ActiveRef | null {
  const raw = localStorage.getItem(KEYS.active);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ActiveRef;
  } catch {
    return null;
  }
}

export function setActive(ref: ActiveRef | null): void {
  if (ref) localStorage.setItem(KEYS.active, JSON.stringify(ref));
  else localStorage.removeItem(KEYS.active);
}

export function getUserId(): string {
  return localStorage.getItem(KEYS.userId) || 'orseni';
}

export function setUserId(v: string): void {
  localStorage.setItem(KEYS.userId, v);
}

export function getTheme(): 'light' | 'dark' | 'auto' {
  const v = localStorage.getItem(KEYS.theme);
  if (v === 'light' || v === 'dark' || v === 'auto') return v;
  return 'auto';
}

export function setTheme(value: 'light' | 'dark' | 'auto'): void {
  localStorage.setItem(KEYS.theme, value);
  applyTheme(value);
}

export function applyTheme(value: 'light' | 'dark' | 'auto'): void {
  const root = document.documentElement;
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const effective = value === 'auto' ? (mql.matches ? 'dark' : 'light') : value;
  root.classList.toggle('dark', effective === 'dark');
}

export function getShowSteps(): boolean {
  return localStorage.getItem(KEYS.showSteps) !== 'false';
}

export function setShowSteps(v: boolean): void {
  localStorage.setItem(KEYS.showSteps, String(v));
}

export function getShowReasoning(): boolean {
  return localStorage.getItem(KEYS.showReasoning) !== 'false';
}

export function setShowReasoning(v: boolean): void {
  localStorage.setItem(KEYS.showReasoning, String(v));
}

// Toggle "Supervisor" no chat: quando ON, mostra os turnos do supervisor
// (team_id sem agent_id) com estilo distinto. Default OFF — usuário quer ver
// só a resposta final do subagent na conversa.
export function getShowSupervisor(): boolean {
  return localStorage.getItem(KEYS.showSupervisor) === 'true';
}

export function setShowSupervisor(v: boolean): void {
  localStorage.setItem(KEYS.showSupervisor, String(v));
}

// Mapa { "<endpointUrl>::<targetId>": sessionId } pra manter conversa colada
// no par (agente, endpoint). Reset apenas via "Nova conversa".
type ChatSessions = Record<string, string>;

function readChatSessions(): ChatSessions {
  const raw = localStorage.getItem(KEYS.chatSessions);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as ChatSessions;
  } catch {
    return {};
  }
}

function writeChatSessions(map: ChatSessions): void {
  localStorage.setItem(KEYS.chatSessions, JSON.stringify(map));
}

export function chatSessionKey(endpointUrl: string, targetId: string): string {
  return `${endpointUrl}::${targetId}`;
}

export function getChatSessionId(key: string): string | null {
  return readChatSessions()[key] ?? null;
}

export function setChatSessionId(key: string, sessionId: string): void {
  const map = readChatSessions();
  map[key] = sessionId;
  writeChatSessions(map);
}
